/**
 * @file Organism: ErrorBoundary
 * @description Generic error boundary that catches thrown errors from child components
 * and renders ErrorPage. Components can `throw new Error("message")` during render
 * and this boundary will display the error with a retry option.
 */

import type React from 'react';
import { Component, type ReactNode } from 'react';
import ErrorPage from '../../pages/ErrorPage';
import { analytics } from '../../services/analyticsService';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);

    const errorContext = error.message?.includes('Loading chunk')
      ? 'ChunkLoadingError'
      : 'ErrorBoundary';

    analytics.trackException(
      error,
      errorContext,
      'ErrorBoundary',
      undefined,
      'error_boundary',
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });

    if (this.props.onRetry) {
      this.props.onRetry();
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          message={
            this.state.error?.message ||
            this.props.fallbackMessage ||
            'Something went wrong. Please try again.'
          }
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
